import React, { useEffect, useState, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Landing route for the Google Calendar OAuth redirect
 * (https://ol.semprion.com/gcal-oauth-callback?code=...&state=...).
 * Reads code + state, derives the org from the state prefix, and POSTs to the
 * backend to complete the token exchange. Without this route the SPA falls
 * through to /dashboard and the code is silently discarded.
 */
export default function GCalOAuthCallback() {
  const { search } = useLocation();
  const [phase, setPhase] = useState('working');
  const [text, setText] = useState('Completing Google Calendar connection…');
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const params = new URLSearchParams(search);
    const code = params.get('code');
    const oauthState = params.get('state') || '';
    const gErr = params.get('error');
    if (gErr) { setPhase('error'); setText(`Google returned: ${gErr}`); return; }
    if (!code) { setPhase('error'); setText('No authorization code in the callback URL.'); return; }
    const orgId = oauthState.split('::')[0];
    if (!orgId) { setPhase('error'); setText('Missing organization in the OAuth state parameter.'); return; }
    (async () => {
      try {
        const { data } = await axios.post(
          `${API}/api/game-schedule/${orgId}/gcal-oauth/callback`,
          { code, state: oauthState },
          { withCredentials: true }
        );
        setPhase('ok');
        setText(`Connected Google Calendar as ${data.connected_account_email}`);
      } catch (e) {
        setPhase('error');
        setText(e.response?.data?.detail || e.message || 'Connection failed.');
      }
    })();
  }, [search]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6" data-testid="gcal-oauth-callback-page">
      <div className="max-w-md w-full bg-slate-900 border border-slate-700 rounded-xl p-8 text-center space-y-4">
        <h1 className="text-lg font-bold text-slate-100">Google Calendar connection</h1>
        {phase === 'working' && (
          <div className="flex flex-col items-center gap-3 text-slate-300" data-testid="gcal-cb-working">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            <p className="text-sm">{text}</p>
          </div>
        )}
        {phase === 'ok' && (
          <div className="flex flex-col items-center gap-3 text-emerald-400" data-testid="gcal-cb-ok">
            <CheckCircle2 className="h-8 w-8" />
            <p className="text-sm">{text}</p>
          </div>
        )}
        {phase === 'error' && (
          <div className="flex flex-col items-center gap-3 text-red-400" data-testid="gcal-cb-error">
            <AlertTriangle className="h-8 w-8" />
            <p className="text-sm">{text}</p>
          </div>
        )}
        <Link to="/dashboard" className="inline-block text-xs text-slate-400 hover:text-slate-200 underline" data-testid="gcal-cb-back">
          Return to dashboard
        </Link>
      </div>
    </div>
  );
}
